'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Input, Pagination } from '@mantine/core';
import {
    IconSearch,
    IconX,
    IconSortDescending,
    IconLayoutGrid,
    IconCategory,
    IconArrowDown,
    IconUser,
    IconTag,
} from '@tabler/icons-react';

import styles from './NeumoArticlesExplorer.module.scss';
import { fetchArticles } from '@/src/helpers/fetch-articles';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';

type Props = {
    lang: 'fr';
};

type ViewMode = 'filter' | 'sections';
type SortMode = 'newest' | 'az';

interface Article {
    id?: string;
    _id?: string;
    slug: string;
    title: any;
    tags: string[];
    mainImage: string;
    createdAt: string;
    description: any;
    author?: string;
    category?: string;
}

function getLocalizedText(val: any, lang: 'fr') {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val?.[lang] ?? val?.fr ?? val?.en ?? '';
}

function normalizeCategory(cat?: string) {
    const c = (cat || '').trim();
    return c || 'default';
}

function prettifyCategory(cat: string, pageCopy: any) {
    if (cat === 'default') return pageCopy.defaultCategoryLabel;
    return cat.replaceAll('_', ' ').replaceAll('-', ' ');
}

function titleToSlugForYourDecoder(title: string) {
    return encodeURIComponent(
        (title || '')
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/ /g, '-')
    );
}


export default function NeumoArticlesExplorer({ lang }: Props) {
    const dictionary = useDictionary();
    const pageCopy = dictionary.pages.articles;

    const [articles, setArticles] = useState<Article[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [viewMode, setViewMode] = useState<ViewMode>('filter');
    const [sortMode, setSortMode] = useState<SortMode>('newest');
    const [selectedCategory, setSelectedCategory] = useState<string>('article');
    const [searchQuery, setSearchQuery] = useState('');

    const listRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const run = async () => {
            const res: any = await fetchArticles(
                page,
                searchQuery,
                lang,
                undefined, // url
                selectedCategory === 'all' ? undefined : selectedCategory
            );

            setArticles(res.articles ?? []);
            setTotalPages(res.totalPages ?? 1);
        };

        run();
    }, [page, searchQuery, lang, selectedCategory]);

    const categories = useMemo(() => {
        const cats = Array.from(new Set(articles.map((a: any) => normalizeCategory(a.category))));
        return cats.sort((a, b) =>
            prettifyCategory(a, pageCopy).localeCompare(prettifyCategory(b, pageCopy), lang)
        );
    }, [articles, pageCopy, lang]);

    useEffect(() => {
        if (selectedCategory !== 'all' && categories.length && !categories.includes(selectedCategory)) {
            setSelectedCategory('all');
        }
    }, [categories, selectedCategory]);

    const sortedArticles = useMemo(() => {
        const list = [...articles];

        if (sortMode === 'newest') {
            list.sort((a: any, b: any) => {
                const da = new Date(a.createdAt || 0).getTime();
                const db = new Date(b.createdAt || 0).getTime();
                return db - da;
            });
        } else {
            list.sort((a: any, b: any) => {
                const ta = getLocalizedText(a.title, lang).toLowerCase();
                const tb = getLocalizedText(b.title, lang).toLowerCase();
                return ta.localeCompare(tb, lang);
            });
        }

        return list;
    }, [articles, sortMode, lang]);

    const filteredArticles = useMemo(() => {
        return selectedCategory === 'all'
            ? sortedArticles
            : sortedArticles.filter((a: any) => normalizeCategory(a.category) === selectedCategory);
    }, [sortedArticles, selectedCategory]);

    const groupedByCategory = useMemo(() => {
        const map = new Map<string, Article[]>();
        for (const a of sortedArticles) {
            const key = normalizeCategory((a as any).category);
            map.set(key, [...(map.get(key) || []), a]);
        }
        return Array.from(map.entries()).sort(([a], [b]) =>
            prettifyCategory(a, pageCopy).localeCompare(prettifyCategory(b, pageCopy), lang)
        );
    }, [sortedArticles, pageCopy, lang]);

    const preview = useMemo(() => sortedArticles.slice(0, 3), [sortedArticles]);

    const clearSearch = () => {
        setSearchQuery('');
        setPage(1);
    };

    const scrollToList = () => {
        listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const resultsCount = viewMode === 'filter' ? filteredArticles.length : sortedArticles.length;

    console.log(viewMode, selectedCategory, searchQuery, resultsCount, filteredArticles, groupedByCategory);

    return (
        <section className={styles.hero} aria-labelledby="articles-title">
            <div className={styles.container}>
                <div className={styles.aiHeadVisual} aria-hidden="true">
                    <div className={styles.aiHeadGlow} />
                </div>

                <div className={styles.surface}>
                    <div className={styles.surfaceContainer}>
                        <header className={styles.header}>
                            <p className={styles.eyebrow}>{pageCopy.eyebrow}</p>

                            <h1 id="articles-title" className={styles.title}>
                                {pageCopy.title}
                            </h1>

                            <p className={styles.subtitle}>{pageCopy.search}</p>

                            {pageCopy.seoIntro ? (
                                <div className={styles.seoIntro}>
                                    <p>{pageCopy.seoIntro}</p>
                                </div>
                            ) : null}
                        </header>

                        {/* LISTE */}
                        <div ref={listRef} />

                        <section className={styles.realizations} aria-label={pageCopy.listAriaLabel}>
                            <div className={styles.blockHeader}>
                                <h2 className={styles.blockTitle}>{pageCopy.listTitle}</h2>
                                <p className={styles.blockSubtitle}>{pageCopy.listSubtitle}</p>
                            </div>

                            {/* OPTIONS TOP */}
                            <div className={styles.options}>
                                <div className={styles.optionsLeft}>
                                    <div className={styles.optionsLabel}>
                                        <IconCategory size={16} />
                                        <span>{pageCopy.categoryTitle}</span>
                                    </div>

                                    <div className={styles.chipsRow} role="list" aria-label={pageCopy.categoriesAriaLabel}>
                                        <button
                                            type="button"
                                            className={`${styles.chip} ${selectedCategory === 'all' ? styles.chipActive : ''}`}
                                            onClick={() => {
                                                setSelectedCategory('all');
                                                setPage(1);
                                            }}
                                            role="listitem"
                                        >
                                            {pageCopy.allLabel}
                                        </button>

                                        {categories.map((cat) => (
                                            <button
                                                key={cat}
                                                type="button"
                                                className={`${styles.chip} ${selectedCategory === cat ? styles.chipActive : ''}`}
                                                onClick={() => {
                                                    setSelectedCategory(cat);
                                                    setPage(1);
                                                    setViewMode('filter');
                                                }}
                                                role="listitem"
                                            >
                                                {prettifyCategory(cat, pageCopy)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className={styles.optionsRight}>
                                    <div className={styles.pills}>
                                        <button
                                            type="button"
                                            className={`${styles.pill} ${viewMode === 'filter' ? styles.pillActive : ''}`}
                                            onClick={() => setViewMode('filter')}
                                            aria-pressed={viewMode === 'filter'}
                                        >
                                            <IconLayoutGrid size={16} />
                                            <span>{pageCopy.viewFilterLabel}</span>
                                        </button>

                                        <button
                                            type="button"
                                            className={`${styles.pill} ${viewMode === 'sections' ? styles.pillActive : ''}`}
                                            onClick={() => {
                                                setViewMode('sections');
                                                setSelectedCategory('all');
                                            }}
                                            aria-pressed={viewMode === 'sections'}
                                        >
                                            <IconCategory size={16} />
                                            <span>{pageCopy.viewSectionsLabel}</span>
                                        </button>

                                        <button
                                            type="button"
                                            className={styles.pill}
                                            onClick={() => setSortMode((s) => (s === 'newest' ? 'az' : 'newest'))}
                                            aria-label={pageCopy.sortToggleAriaLabel}
                                            title={pageCopy.sortToggleTitle}
                                        >
                                            <IconSortDescending size={16} />
                                            <span>{sortMode === 'newest' ? pageCopy.sortNewestLabel : pageCopy.sortAzLabel}</span>
                                        </button>
                                    </div>

                                    <div className={styles.resultsInfo}>
                                        <span className={styles.resultsNumber}>{resultsCount}</span>
                                        <span>{pageCopy.resultsLabel}</span>
                                        {selectedCategory !== 'all' && viewMode === 'filter' ? (
                                            <span className={styles.resultsMeta}>· {prettifyCategory(selectedCategory, pageCopy)}</span>
                                        ) : null}
                                        {searchQuery ? <span className={styles.resultsMeta}>· “{searchQuery}”</span> : null}
                                    </div>
                                </div>
                            </div>

                            {/* SEARCH */}
                            <div className={styles.searchRow}>
                                <Input
                                    placeholder={pageCopy.searchPlaceholder}
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setPage(1);
                                    }}
                                    style={{ width: '100%' }}
                                    leftSection={<IconSearch size={18} />}
                                    classNames={{ input: styles.searchInput }}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className={styles.clearBtn}
                                        onClick={clearSearch}
                                        aria-label={pageCopy.clearSearchAriaLabel}
                                        title={pageCopy.clearSearchTitle}
                                    >
                                        <IconX size={18} />
                                    </button>
                                )}
                            </div>

                            {/* CONTENT */}
                            <div className={styles.content}>
                                {viewMode === 'filter' ? (
                                    <>
                                        <div className={styles.grid} role="list" aria-label={pageCopy.articlesListAriaLabel}>
                                            {filteredArticles.map((a) => (
                                                <ArticleCard key={a.title} article={a} lang={lang} pageCopy={pageCopy} />
                                            ))}
                                        </div>

                                        <div className={styles.paginationRow}>
                                            <Pagination
                                                value={page}
                                                onChange={setPage}
                                                total={totalPages}
                                                radius="xl"
                                                color="dark"
                                                aria-label="Pagination"
                                                getItemProps={(p) => ({
                                                    "aria-label": `Go to page ${p}`,
                                                })}
                                                getControlProps={(control) => ({
                                                    "aria-label":
                                                        control === "next"
                                                            ? "Next page"
                                                            : control === "previous"
                                                                ? "Previous page"
                                                                : control === "first"
                                                                    ? "First page"
                                                                    : "Last page",
                                                })}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.sections}>
                                        {groupedByCategory.map(([cat, items]) => (
                                            <section key={cat} className={styles.section} aria-label={prettifyCategory(cat, pageCopy)}>
                                                <div className={styles.sectionHeader}>
                                                    <h3 className={styles.sectionTitle}>{prettifyCategory(cat, pageCopy)}</h3>
                                                    <p className={styles.sectionHint}>{pageCopy.sectionHint}</p>
                                                </div>

                                                <div className={styles.grid} role="list" aria-label={pageCopy.articlesListAriaLabel}>
                                                    {items.map((a) => (
                                                        <ArticleCard key={a.title} article={a} lang={lang} pageCopy={pageCopy} />
                                                    ))}
                                                </div>
                                            </section>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>

                        <footer className={styles.footerSeo}>
                            <p>{pageCopy.footerSeoText}</p>
                            <Link href={`/${lang}/contact`} className={styles.footerLink}>
                                {pageCopy.footerCtaLabel}
                            </Link>
                        </footer>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ArticleCard({ article, lang, pageCopy }: { article: Article; lang: 'fr'; pageCopy: any }) {
    const title = getLocalizedText(article?.title, lang);
    const description = getLocalizedText(article?.description, lang);
    const slug = article?.slug;
    const href = `/${lang}/articles/${encodeURIComponent(slug)}`;

    const date = article?.createdAt
        ? new Date(article.createdAt).toLocaleDateString(pageCopy.dateLocale)
        : '';

    const category = prettifyCategory(normalizeCategory(article?.category), pageCopy);

    return (
        <article className={styles.card} role="listitem">
            <Link href={href} className={styles.cardLink} aria-label={title}>
                <div className={styles.media}>
                    <Image
                        src={article?.mainImage ?? ''}
                        alt={title}
                        fill
                        sizes="(max-width: 768px) 100vw, 33vw"
                        style={{ objectFit: 'cover' }}
                    />
                    <div className={styles.mediaOverlay} aria-hidden="true" />
                    <div className={styles.badge}>{category}</div>
                </div>

                <div className={styles.cardBody}>
                    <h3 className={styles.cardTitle}>{title}</h3>
                    <p className={styles.cardDesc}>{description}</p>

                    <div className={styles.cardBottom}>
                        <div className={styles.metaLeft}>
                            {article?.author ? (
                                <span className={styles.metaItem}>
                                    <IconUser size={14} />
                                    {article.author}
                                </span>
                            ) : null}
                            {date ? <span className={styles.metaItem}>· {date}</span> : null}
                        </div>

                        <span className={styles.cta}>{pageCopy.cardCtaLabel}</span>
                    </div>

                    {Array.isArray(article?.tags) && article.tags.length ? (
                        <div className={styles.tags}>
                            <IconTag size={14} />
                            <span>
                                {pageCopy.tagsPrefixLabel}{' '}
                                {article.tags.slice(0, pageCopy.tagsMaxCount).join(pageCopy.tagsSeparator)}
                            </span>
                        </div>
                    ) : null}
                </div>
            </Link>
        </article>
    );
}
